import { useState, useEffect, useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useModalStore } from '../../stores/modal.store';
import { useAuthStore } from '../../stores/auth.store';
import { useUrlState } from '../../hooks/useUrlState';
import { OrdinaFiltra, useFiltri, useOrdina, type FiltroDef, type OpzioneOrdina } from '../../components/ui/OrdinaFiltra';
import { Icon } from '../../components/ui/Icon';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { Chip } from '../../components/ui/Chip';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { Avatar } from '../../components/ui/Avatar';
import { EmptyState } from '../../components/ui/EmptyState';
import { Pagination } from '../../components/ui/Pagination';
import { SkeletonKPIRow, SkeletonTable } from '../../components/ui/Skeleton';
import { MobileFilters } from '../../components/ui/MobileFilters';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { fmtEur, fmtDate } from '../../lib/utils';
import { adminLinks } from '../../lib/adminRoutes';
import './suppliers.css';

interface Supplier {
  id: string; fullName: string; vat: string | null; email: string | null; city: string | null;
  hourlyRate: string | null; active: boolean; skills: { name: string; level: number }[];
  /** Persona fisica (professionista) invece che società: lo dice l'anagrafica FattureInCloud. */
  isPerson: boolean;
  /** Valorizzato quando lo stesso soggetto è anche un cliente: il rimando alla sua scheda CRM. */
  alsoClient: { id: string; shortName: string } | null;
  payablesCount: number;
  payablesTotal: number;
  /** La data dell'ultima fattura che ci ha mandato: null se non ce n'è nessuna. */
  lastPayableDate: string | null;
}

interface PaginatedSuppliers {
  items: Supplier[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/**
 * Ordina per (24/9). La lista è paginata, quindi ordina l'API (`sort`). Il
 * primo è l'ordine di sempre.
 */
const ORDINI: OpzioneOrdina[] = [
  { key: 'nome', label: 'Nome dalla A alla Z, prima gli attivi' },
  { key: 'costo', label: 'Prima chi ci è costato di più' },
  { key: 'fatture', label: 'Prima chi ci ha mandato più fatture' },
  { key: 'ultima', label: 'Prima chi ci ha fatturato più di recente' },
  { key: 'citta', label: 'Città dalla A alla Z' },
];

/**
 * Le città dell'anagrafica, una volta sola ciascuna: "CATANIA" e "Catania"
 * sono lo stesso posto (le schede arrivano da FattureInCloud scritte come
 * capita). Si mostra la grafia normale se c'è, altrimenti quella maiuscola
 * rimessa in minuscolo con l'iniziale grande. L'API confronta senza badare
 * alle maiuscole, quindi il valore scelto le prende tutte.
 */
function cittaDellAnagrafica(items: Supplier[]): Array<{ value: string; label: string; count: number }> {
  const gruppi = new Map<string, { nomi: string[]; count: number }>();
  for (const s of items) {
    const c = s.city?.trim();
    if (!c) continue;
    const k = c.toLocaleLowerCase('it');
    const g = gruppi.get(k) ?? { nomi: [], count: 0 };
    g.nomi.push(c);
    g.count += 1;
    gruppi.set(k, g);
  }
  const bello = (nomi: string[]) => {
    const normale = nomi.find((n) => n !== n.toLocaleUpperCase('it'));
    if (normale) return normale;
    return nomi[0]!.toLocaleLowerCase('it').replace(/(^|[\s'’-])(\p{L})/gu, (_m, sep: string, l: string) => sep + l.toLocaleUpperCase('it'));
  };
  return [...gruppi.values()]
    .map((g) => ({ value: bello(g.nomi), label: bello(g.nomi), count: g.count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'it'));
}

export function SuppliersPage() {
  const navigate = useNavigate();
  const openModal = useModalStore((s) => s.open);
  const user = useAuthStore((s) => s.user);
  const showMargin = !!user?.role.canSeeMargin;
  // La ricerca sta nell'URL come nelle altre liste: tornando dalla scheda la
  // lista è com'era, e un link la riapre uguale.
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [debSearch, setDebSearch] = useState(search);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  useEffect(() => {
    const t = setTimeout(() => setDebSearch(search), 220);
    return () => clearTimeout(t);
  }, [search]);

  // Query separata senza filtri: serve per le KPI in alto (attivi, skill,
  // tariffa media, totale), che devono restare stabili qualunque cosa si
  // cerchi, e per le voci di "Filtra per" con i loro conteggi.
  const { data: allData } = useQuery({
    queryKey: ['suppliers', 'stats'],
    queryFn: () => api.get<PaginatedSuppliers>('/suppliers?pageSize=200'),
  });

  const ordina = useOrdina(ORDINI);
  const tutti = allData?.items;
  const defsFiltri = useMemo<FiltroDef[]>(() => {
    const lista = tutti ?? [];
    const conta = (f: (s: Supplier) => boolean) => (tutti ? lista.filter(f).length : undefined);
    return [
      {
        param: 'stato', label: 'Stato della scheda',
        opzioni: [
          { value: 'attivi', label: 'Solo i professionisti attivi', count: conta((s) => s.active) },
          { value: 'non-attivi', label: 'Solo i professionisti non più attivi', count: conta((s) => !s.active) },
        ],
      },
      { param: 'citta', label: 'In che città sta', multi: true, opzioni: cittaDellAnagrafica(lista) },
    ];
  }, [tutti]);
  const filtri = useFiltri(defsFiltri);
  const stato = filtri.valore('stato');
  const citta = filtri.valori.citta ?? [];

  // Cambiando ricerca, ordine o filtri si torna a pagina 1 (altrimenti rischi una pagina vuota)
  useEffect(() => { setPage(1); }, [debSearch, ordina.key, filtri.chiave]);

  const params = new URLSearchParams();
  if (debSearch) params.set('search', debSearch);
  if (!ordina.predefinita) params.set('sort', ordina.key);
  if (stato) params.set('stato', stato);
  if (citta.length) params.set('citta', citta.join(','));
  params.set('page', String(page));
  params.set('pageSize', String(PAGE_SIZE));
  const qs = params.toString();

  const { data, isLoading } = useQuery({
    queryKey: ['suppliers', debSearch, ordina.key, filtri.chiave, page],
    queryFn: () => api.get<PaginatedSuppliers>(`/suppliers?${qs}`),
    // Cambiando ordine la tabella resta dov'è finché arriva la nuova: niente
    // lampo di scheletro per ogni scelta nel menu.
    placeholderData: keepPreviousData,
  });
  const filtrata = !!search.trim() || filtri.attivi > 0;

  return (
    <div className="page">
      {/* ── RIFACIMENTO EDITORIALE: titolo grande e leggero, hairline aurora,
            numeri NUDI sul foglio (stat-band), zero scatole ── */}
      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div>
          <h1 className="ed-title">Professionisti</h1>
          <div className="ed-sub">
            Le persone che lavorano con noi e ci fatturano. Le società fornitrici stanno in{' '}
            {/* sup-hit allarga il bersaglio a 44px sul telefono senza gonfiare
                il testo: è un link dentro una frase, ingrandirlo la spezzerebbe. */}
            <a className="underline cursor-pointer sup-hit" onClick={() => navigate(adminLinks.fornitori())}>Amministrazione, Spese</a>.
          </div>
        </div>
        {/* d-only: sul telefono l'azione principale non sta in cima, dove il
            pollice non arriva, ma nella barra in fondo (mobile.md §4). */}
        <div className="flex gap-2 pb-1 d-only">
          <button className="btn btn-primary" onClick={() => openModal('newVendor')}><Icon name="plus" /> Nuovo professionista</button>
          <ActionsMenu
            variant="ghost"
            items={[
              { key: 'export', label: 'Esporta', icon: 'export', onClick: () => openModal('export', { what: 'Professionisti' }) },
            ]}
          />
        </div>
      </div>

      {isLoading && !data ? <SkeletonKPIRow count={4} /> : (
      <div className="stat-band">
        <div>
          <div className="st-label">Professionisti attivi</div>
          <div className="st-value"><AnimatedNumber value={allData?.items.filter((s) => s.active).length ?? 0} /></div>
          <div className="st-delta">di cui {(allData?.items ?? []).filter((s) => s.isPerson).length} persone fisiche</div>
        </div>
        <div>
          {/* Quanto ci costano davvero: è il numero che dice se l'anagrafica
              serve a qualcosa, molto più delle skill registrate a mano. */}
          <div className="st-label">Speso con loro</div>
          <div className="st-value"><span className="u">€ </span><AnimatedNumber value={(allData?.items ?? []).reduce((a, s) => a + s.payablesTotal, 0)} format={fmtEur} /></div>
          <div className="st-delta">{(allData?.items ?? []).reduce((a, s) => a + s.payablesCount, 0)} fatture ricevute</div>
        </div>
        {showMargin
          ? (() => {
              const rates = (allData?.items ?? []).map((s) => Number(s.hourlyRate ?? 0)).filter((r) => r > 0);
              const avg = rates.length ? Math.round(rates.reduce((a, b) => a + b, 0) / rates.length) : 0;
              return (
                <div>
                  <div className="st-label">Tariffa media</div>
                  {rates.length
                    ? <div className="st-value"><span className="u">€</span><AnimatedNumber value={avg} /></div>
                    : <div className="st-value mute-2">—</div>}
                  {/* Su quante è calcolata: la media di 3 tariffe su 105 schede
                      non è "la tariffa media dei fornitori", e va detto. */}
                  <div className="st-delta">{rates.length ? `all'ora · su ${rates.length} con tariffa` : 'nessuna tariffa impostata'}</div>
                </div>
              );
            })()
          : (
            <div>
              <div className="st-label">Tariffa media</div>
              <div className="st-value mute-2">•••</div>
              <div className="st-delta">Riservato</div>
            </div>
          )}
        <div>
          {/* Chi compare da tutte e due le parti: il numero che dice quanto
              conta la vista unificata cliente/fornitore che arriverà. */}
          <div className="st-label">Anche clienti</div>
          <div className="st-value"><AnimatedNumber value={(allData?.items ?? []).filter((s) => s.alsoClient).length} /></div>
          <div className="st-delta">su {allData?.items.length ?? 0} anagrafiche</div>
        </div>
      </div>
      )}

      {/* mb-4 + flex-wrap: è la forma canonica della barra filtri (19 schermate
          su 21). Senza mb-4 il margine resta 0 e, collassando con
          .ed-section { margin-top: 4 }, la barra finiva a 4px dalla tabella
          invece dei 16 di tutte le altre. */}
      <MobileFilters attivi={(search.trim() ? 1 : 0) + filtri.attivi} label="Cerca, ordina e filtra">
      <div className="filterbar filterbar--float mb-4 flex-wrap">
        <div className="tb-search" style={{ width: 280, margin: 0 }}>
          <Icon name="search" size={12} />
          <input placeholder="Cerca per nome, email, città…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="spacer" />
        <span className="mono mute text-[12px]">{data?.total ?? 0} risultati</span>
        <OrdinaFiltra ordina={ordina} filtri={filtri} />
      </div>
      </MobileFilters>

      {/* Full-bleed: la lista vive sul foglio, senza gabbia */}
      <div className="ed-section">
        {/* tbl--card: sotto i 768px ogni riga diventa una scheda. Sul computer
            non cambia niente — la classe non ha regole fuori dalla media query. */}
        <table className="tbl tbl--ed tbl--card">
          {/* Niente colonne-codice in lista: P.IVA resta nel dettaglio; l'email scende
              come riga secondaria del nome (cell-2l) invece di occupare una colonna. */}
          <thead>
            <tr>
              <th style={{ width: 30 }} /><th>Nome</th><th>Città</th>
              <th className="num">Fatture</th>
              <th className="num">Speso</th>
              {showMargin && <th className="num">Tariffa</th>}
              <th>Stato</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && !data && <SkeletonTable rows={5} cols={showMargin ? 7 : 6} />}
            {(data?.items ?? []).map((s) => (
              <tr key={s.id} onClick={() => navigate(`/suppliers/${s.id}`)}>
                {/* Le iniziali senza il nome accanto non dicono niente: sul
                    telefono spariscono, il nome è già il titolo della scheda. */}
                <td data-m="off"><Avatar initials={s.fullName.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase()} /></td>
                <td data-m="hd" className="cell-2l">
                  <div className="p flex items-center gap-2">
                    <span className="truncate">{s.fullName}</span>
                    {/* Soggetto che è anche cliente: si segna, non si fonde.
                        Il click porta alla sua scheda CRM senza passare da qui. */}
                    {s.alsoClient && (
                      <span
                        className="chip-label"
                        onClick={(e) => { e.stopPropagation(); navigate(`/clients/${s.alsoClient!.id}`); }}
                        title={`Anche cliente · ${s.alsoClient.shortName}`}
                      >
                        anche cliente
                      </span>
                    )}
                  </div>
                  {/* Riga secondaria: la mail se c'è, altrimenti la P.IVA —
                      su 105 anagrafiche arrivate da FIC la mail manca quasi
                      sempre, e una riga vuota non direbbe niente. */}
                  <div className="s">{s.email ?? s.vat ?? (s.isPerson ? 'professionista' : '—')}</div>
                </td>
                <td data-m="key" data-l="Città">{s.city ?? '—'}</td>
                <td data-m="key" data-l="Fatture" className="num mono mute">
                  {s.payablesCount || '—'}
                  {/* Ordinando per "più di recente" la data si deve vedere,
                      altrimenti l'ordine sembra a caso. */}
                  {ordina.key === 'ultima' && s.lastPayableDate && (
                    <div className="text-[11px]" title="La data dell'ultima fattura che ci ha mandato">ultima {fmtDate(s.lastPayableDate)}</div>
                  )}
                </td>
                <td data-m="key" data-l="Speso" className="num tbl-metric">{s.payablesTotal ? `€ ${fmtEur(s.payablesTotal)}` : '—'}</td>
                {/* Tariffa solo dove esiste: "€ 0/h" su 100 righe su 105 è
                    rumore che fa sembrare gratis chi gratis non è. Sul telefono
                    resta fuori: è quasi sempre vuota e sta nella scheda. */}
                {showMargin && <td data-m="off" className={`num ${Number(s.hourlyRate ?? 0) > 0 ? 'tbl-metric accent' : 'mono mute'}`}>{Number(s.hourlyRate ?? 0) > 0 ? `€ ${fmtEur(s.hourlyRate ?? 0)}/h` : '—'}</td>}
                <td data-m="key" data-l="Stato"><Chip tone={s.active ? 'ok' : 'ghost'} dot>{s.active ? 'Attivo' : 'Inattivo'}</Chip></td>
              </tr>
            ))}
            {!data?.items.length && (
              <tr>
                <td colSpan={showMargin ? 7 : 6} style={{ padding: 0 }}>
                  <EmptyState
                    brand
                    title={filtrata ? 'Nessun risultato' : 'Nessun professionista'}
                    description={filtrata
                      ? (filtri.attivi > 0 ? 'Togli un filtro o cambia la ricerca.' : 'Prova a modificare la ricerca.')
                      : 'Aggiungi i collaboratori esterni e le agenzie partner per assegnarli ai progetti.'}
                    action={filtri.attivi > 0 ? (
                      <button className="btn" onClick={filtri.clear}>Togli i filtri</button>
                    ) : !filtrata ? (
                      <button className="btn btn-primary d-only" onClick={() => openModal('newVendor')}>
                        <Icon name="plus" size={12} /> Aggiungi il primo
                      </button>
                    ) : undefined}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {data && (
        <Pagination page={data.page} pageSize={data.pageSize} total={data.total} totalPages={data.totalPages} onPage={setPage} />
      )}

      {/* Telefono: la stessa azione che in cima è d-only, qui dove arriva il
          pollice. Il fondo pagina se lo allunga da solo .page:has(.m-actionbar). */}
      <DesktopOnly
        cosa="La creazione di un nuovo professionista"
        perche="richiede anagrafica fiscale, competenze e tariffe; dal telefono puoi consultare, contattare e allegare documenti."
      >
        <div className="m-actionbar m-only">
          <button className="btn btn-primary" onClick={() => openModal('newVendor')}>
            <Icon name="plus" /> Nuovo professionista
          </button>
        </div>
      </DesktopOnly>
    </div>
  );
}
